import { useEffect, useMemo } from "react";
import Select from "react-select";
import { useParams } from "react-router-dom";
import ModalGroupingTable from "../components/ModalGroupingTable";
import CostAllocation from "../components/CostAllocation.js";
import { useLazyGetDropDownsForProductsAndServicesQuery } from "../../../../../../app/services/productAndService/productAndServiceApi";
import { useLazyGetProductDetailsListByProductInformationIdQuery } from "../../../../../../app/services/CertificateGrouping/certificateGroupingApi";
import {
  useDownloadCostAllocationDocMutation,
  useLazyGetCertificateGroupingDetailsForGWPConcreteByIdQuery,
} from "../../../../../../app/services/selfEvalutionReviewService/selfEvalutionReviewServiceApi";
import { decryptAESID } from "../../../../../../utils/Encryption";
import ToastService from "../../../../../../services/toastService/ToastService";

const TICK_OPTIONS = [
  { label: "1 Tick", value: 1 },
  { label: "2 Ticks", value: 2 },
  { label: "3 Ticks", value: 3 },
  { label: "4 Ticks", value: 4 },
];

const toOptions = (items = [], dropdownName) =>
  items
    .filter((item) => item.dropdownName === dropdownName)
    .map((item) => ({ value: item.id, label: item.name }));

const findOption = (options, value) =>
  options.find((option) => option.value === value) ?? null;

const splitCertificateNumber = (certificateNumber) => {
  const value = String(certificateNumber ?? "");
  const slashIndex = value.lastIndexOf("/");
  if (slashIndex === -1) return { base: value, suffix: 0 };
  return {
    base: value.slice(0, slashIndex),
    suffix: Number(value.slice(slashIndex + 1)) || 0,
  };
};

const buildCostAllocationRows = (products, allocations, itemCodeOptions) => {
  const labels = [
    ...new Set(
      products.map(({ applicationNumber, certPrefix }) =>
        certPrefix ? `${applicationNumber}/${certPrefix}` : applicationNumber
      )
    ),
  ];

  return labels.map((label) => {
    const { base, suffix } = splitCertificateNumber(label);
    const allocation = allocations.find((item) => {
      const current = splitCertificateNumber(item.certificateNumber);
      return current.base === base && current.suffix === suffix;
    });

    return {
      label,
      value: allocation ? findOption(itemCodeOptions, allocation.itemCode) : null,
      tickValue: allocation ? findOption(TICK_OPTIONS, allocation.tickRating) : null,
      sgbpCertificateGroupingId: allocation?.sgbpCertificateGroupingId ?? 0,
      certificateFileName: allocation?.costAllocFileName ?? allocation?.fileName ?? null,
      certificateFilePath: allocation?.costAllocFilePath ?? allocation?.filePath ?? null,
    };
  });
};

const ReadOnlyTextarea = ({ label, value }) => (
  <div className="mt-2">
    <label className="form-label">{label}</label>
    <textarea className="form-control mt-1" rows="4" value={value} disabled />
  </div>
);

const ReadOnlySelectRow = ({ label, value, options, className = "" }) => (
  <div className={`d-flex align-items-center ${className}`}>
    <label className="form-label mb-0 w-20">{label}</label>
    <div style={{ flex: 1 }}>
      <Select className="custom-checkbox-select" value={value} options={options} isDisabled />
    </div>
  </div>
);

const GWPConcreteCertificate = () => {
  const { id } = useParams();
  const [loadDropdowns, { data: dropdownData }] = useLazyGetDropDownsForProductsAndServicesQuery();
  const [loadProducts, { data: productData }] = useLazyGetProductDetailsListByProductInformationIdQuery();
  const [loadGroupingDetails, { data: groupingData }] = useLazyGetCertificateGroupingDetailsForGWPConcreteByIdQuery();
  const [downloadDoc] = useDownloadCostAllocationDocMutation();

  const applicationId = useMemo(() => decryptAESID(id, false), [id]);

  useEffect(() => {
    if (!(applicationId > 0)) return;
    loadDropdowns();
    loadProducts(applicationId);
    loadGroupingDetails(applicationId);
  }, [applicationId, loadDropdowns, loadProducts, loadGroupingDetails]);

  const products = useMemo(
    () => (Array.isArray(productData) ? productData : productData?.data ?? []),
    [productData]
  );

  const approverOptions = useMemo(() => toOptions(dropdownData, "Approver List"), [dropdownData]);
  const itemCodeOptions = useMemo(() => toOptions(dropdownData, "Items Codes"), [dropdownData]);

  const costAllocationRows = useMemo(
    () =>
      buildCostAllocationRows(
        products,
        groupingData?.getCertificateAndCostAllocationList ?? [],
        itemCodeOptions
      ),
    [products, groupingData, itemCodeOptions]
  );

  const peerReviewer = findOption(approverOptions, groupingData?.peerReviewerId);
  const approver = findOption(approverOptions, groupingData?.approverId);
  const caseOfficerComment = groupingData?.commentFromCaseOfficer ?? "";

  const handleCertificateFileDownload = async (row) => {
    if (!row?.certificateFileName || !row?.certificateFilePath) {
      ToastService.error("No file available to download.");
      return;
    }
    const filePath = row.certificateFilePath;
    try {
      const blob = await downloadDoc({
        SGBPCertificateGroupingId: row.sgbpCertificateGroupingId,
        Type: row.certificateFileName.split(".").pop(),
        Path: filePath,
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        filePath.replace(/^UploadCostAllocationDoc[\\/]/i, "").split(/[\\/]/).pop() ||
        `resource${filePath.match(/\.[^.]+$/)?.[0] ?? ""}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      ToastService.error("File not found");
    }
  };

  return (
    <div className="certificate-grouping-list-page">
      <div className="crud-requirement-list">
        <div className="corporate-member-list">
          <div className="table-title mt-2">
            <h3>Model Grouping</h3>
          </div>
          <ModalGroupingTable type="GWP_CONCRETE" data={products} />

          <div className="table-title mt-4">
            <h3>Certificate and Cost Allocation</h3>
          </div>
          <CostAllocation
            type="GWP_CONCRETE"
            rows={costAllocationRows}
            itemCodeOptions={itemCodeOptions}
            tickOptions={TICK_OPTIONS}
            onFileDownload={handleCertificateFileDownload}
          />

          <div className="table-title mt-5 mb-4">
            <h3>Internal Notes</h3>
          </div>
          <ReadOnlyTextarea label="Comments from Case Officer" value={caseOfficerComment} />

          <div className="table-title mt-5 mb-4">
            <h3>Assign to</h3>
          </div>
          <div className="dropdown-select-wrapper">
            <ReadOnlySelectRow label="Peer Reviewer" value={peerReviewer} options={approverOptions} className="mb-4" />
            <ReadOnlySelectRow label="Approver" value={approver} options={approverOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GWPConcreteCertificate;